import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import usePersistedEnvironmentState from "./usePersistedEnvironmentState";
import { useEnvironmentFlag } from "./useFlag";

/**
 * Syncs agent mode state based on feature flags
 * - If oso-for-agents-ui flag is set, agent mode is forced to true
 * - Otherwise, if oso-for-apps-and-agents-ui flag is not set, agent mode is set to false
 */
export function useAgentModeFromFlags() {
  const [isAgentMode, setIsAgentMode] =
    usePersistedEnvironmentState("is_mi6_mode");
  const showOnlyOsoForAgentsFlag = useEnvironmentFlag("oso-for-agents-ui");
  const showOsoForAppsAndAgentsFlag = useEnvironmentFlag(
    "oso-for-apps-and-agents-ui"
  );

  useEffect(() => {
    if (showOnlyOsoForAgentsFlag) {
      setIsAgentMode(true);
    } else if (!showOsoForAppsAndAgentsFlag) {
      setIsAgentMode(false);
    }
  }, [showOnlyOsoForAgentsFlag, showOsoForAppsAndAgentsFlag, setIsAgentMode]);

  return {
    isAgentMode,
    setIsAgentMode,
    showOnlyOsoForAgentsFlag,
    showOsoForAppsAndAgentsFlag,
  };
}

/**
 * Guards routes based on agent mode
 * - Redirects to /agent-app/dashboard if on non-agent route but in agent mode
 * - Redirects to /dashboard if on agent route but not in agent mode
 * - Returns whether the current page can be viewed
 */
export function useAgentModeGuard() {
  const router = useRouter();
  const [canViewPage, setCanViewPage] = useState(false);
  const { isAgentMode } = useAgentModeFromFlags();

  useEffect(() => {
    const path = router.pathname;
    if (!path.startsWith("/agent-app") && isAgentMode) {
      setCanViewPage(false);
      router.replace("/agent-app/dashboard");
    } else if (path.startsWith("/agent-app") && !isAgentMode) {
      setCanViewPage(false);
      router.replace("/dashboard");
    } else {
      setCanViewPage(true);
    }
  }, [isAgentMode, router]);

  return canViewPage;
}
